/**
 * Pure helpers for package variants + tags (no React, no Redux).
 *
 * A package always has at least one variant; the default one holds the "package price / serves / items"
 * the list screen shows. Tags are referenced by id and resolved against the settings list.
 */
import type { CateringPackage, PackageTag, PackageVariant } from '../data/types';

/** BE limits (variants: max 20, name ≤ 100, serves ≤ 30; tag value ≤ 60). */
export const MAX_PACKAGE_VARIANTS = 20;
/** Longest package name the form accepts (the input stops at it, a counter shows it, and a longer saved name must be shortened before saving). */
export const MAX_PACKAGE_NAME_LENGTH = 60;
export const MAX_VARIANT_NAME_LENGTH = 100;
/** Prod input: serves is a number or a range ("20-30"), so 10 characters is enough. The BE allows 30. */
export const MAX_VARIANT_SERVES_LENGTH = 10;
export const MAX_PACKAGE_TAG_LENGTH = 30;
/** Prod input: a package serves at least 1 guest. */
export const MIN_VARIANT_SERVES = 1;
export const SERVES_MIN_ERROR = `Serves must be at least ${MIN_VARIANT_SERVES}`;

/**
 * Every number in serves ("25", "10-20") must be at least `MIN_VARIANT_SERVES`, so "0", "00" and "0-5" are rejected.
 */
export function servesBelowMinimum(serves: string | null | undefined): boolean {
  const numbers = (serves ?? '').match(/\d+(\.\d+)?/g) ?? [];
  return numbers.some((n) => Number(n) < MIN_VARIANT_SERVES);
}

export const SERVES_REQUIRED_ERROR = 'Serves is required';
/** Placeholder for every Serves input (package form and order / estimate package line). */
export const SERVES_PLACEHOLDER = 'eg. 1, 10, 20-30';

/** Serves takes a number or a range ("1", "10", "20-30"): digits and "-" only — no letters, spaces or other symbols. */
const SERVES_DISALLOWED_CHARS = /[^\d-]/g;
export const SERVES_INVALID_ERROR = 'Serves can have only numbers and -';
export const SERVES_NUMBER_ERROR = 'Serves must include a number';
export const SERVES_TOO_LONG_ERROR = `Serves can be at most ${MAX_VARIANT_SERVES_LENGTH} characters`;

/** What the Serves inputs keep from a keystroke / paste: letters and other symbols are dropped. */
export function sanitizeServesInput(value: string): string {
  return value.replace(SERVES_DISALLOWED_CHARS, '');
}

/**
 * Serves (package form and order / estimate package line): required, at most 10 characters, numbers and - only,
 * at least one number, every number at least 1. The inputs already stop at the max length and drop other
 * characters; this also catches an older saved value ("Family", a 30-digit serves).
 */
export function packageServesError(serves: string | null | undefined): string | undefined {
  const value = (serves ?? '').trim();
  if (!value) return SERVES_REQUIRED_ERROR;
  if (value.length > MAX_VARIANT_SERVES_LENGTH) return SERVES_TOO_LONG_ERROR;
  if (value.replace(SERVES_DISALLOWED_CHARS, '') !== value) return SERVES_INVALID_ERROR;
  if (!/\d/.test(value)) return SERVES_NUMBER_ERROR;
  if (servesBelowMinimum(value)) return SERVES_MIN_ERROR;
  return undefined;
}

/** The default variant (flagged, else the one `defaultVariantId` names, else the first). `undefined` only for a package with no variants. */
export function getDefaultVariant(pkg: Pick<CateringPackage, 'variants' | 'defaultVariantId'>): PackageVariant | undefined {
  return (
    pkg.variants.find((v) => v.isDefault) ??
    pkg.variants.find((v) => v.id === pkg.defaultVariantId) ??
    pkg.variants[0]
  );
}

/** What a variant is called in pickers: its name, else its serves text ("Large", "30-40"). */
export function variantLabel(variant: Pick<PackageVariant, 'name' | 'serves'>): string {
  return variant.name?.trim() || variant.serves.trim();
}

/** "Classic South Indian Breakfast (30-40)" — a package name followed by the variant label in brackets. */
export function variantOptionLabel(pkgName: string, variant: Pick<PackageVariant, 'name' | 'serves'>): string {
  const label = variantLabel(variant);
  return label ? `${pkgName} (${label})` : pkgName;
}

/**
 * A serving option's title in the package screens, in two parts so a list can cut the name and keep the suffix whole:
 * "Package" + " (Large)" when the option has a name, " (Serves 30-40)" when it only has serves, nothing when neither.
 */
export function variantTitleParts(pkgName: string, variant: Pick<PackageVariant, 'name' | 'serves'>): { name: string; suffix: string } {
  const inner = variant.name?.trim() || (variant.serves.trim() ? `Serves ${variant.serves.trim()}` : '');
  return { name: pkgName, suffix: inner ? ` (${inner})` : '' };
}

/** "Package (Serves 30-40)" — `variantTitleParts` as one string. */
export function variantTitle(pkgName: string, variant: Pick<PackageVariant, 'name' | 'serves'>): string {
  const { name, suffix } = variantTitleParts(pkgName, variant);
  return `${name}${suffix}`;
}

/** A package row for the list: only packages with more than one variant show "N serving options". */
export function hasMultipleVariants(pkg: Pick<CateringPackage, 'variants'>): boolean {
  return pkg.variants.length > 1;
}

/** Variant lookup that tolerates a removed variant (returns `undefined`, never throws). */
export function findVariant(pkg: Pick<CateringPackage, 'variants'> | undefined, variantId: string | null | undefined): PackageVariant | undefined {
  if (!pkg || !variantId) return undefined;
  return pkg.variants.find((v) => v.id === variantId);
}

/** Variant to start a new line on: the default one. */
export function lineVariantFor(pkg: CateringPackage, variantId?: string | null): PackageVariant | undefined {
  return findVariant(pkg, variantId) ?? getDefaultVariant(pkg);
}

// ── Ordering / filtering ────────────────────────────────────────────────────────

/** By `sortOrder` (packages without one keep incoming order, after those that have one). The BE already returns this order. */
export function sortPackages(list: readonly CateringPackage[]): CateringPackage[] {
  return list
    .map((pkg, index) => ({ pkg, index }))
    .sort((a, b) => {
      const sa = a.pkg.sortOrder ?? Number.POSITIVE_INFINITY;
      const sb = b.pkg.sortOrder ?? Number.POSITIVE_INFINITY;
      if (sa !== sb) return sa < sb ? -1 : 1;
      return a.index - b.index;
    })
    .map((x) => x.pkg);
}

const norm = (s: string) => s.trim().toLowerCase();

/** What a serving option can be found by: its name, its serves, and "Serves <serves>" as its title reads. */
function variantSearchTexts(variant: Pick<PackageVariant, 'name' | 'serves'>): string[] {
  const serves = variant.serves.trim();
  return [variant.name ?? '', ...(serves ? [serves, `Serves ${serves}`] : [])];
}

/** Case-insensitive: matches the package name, any tag value, any serving option (name / serves) or item name. */
export function packageMatches(pkg: CateringPackage, query: string): boolean {
  const q = norm(query);
  if (!q) return true;
  return (
    norm(pkg.name).includes(q) ||
    pkg.tags.some((t) => norm(t.value).includes(q)) ||
    pkg.variants.some((v) =>
      variantSearchTexts(v).some((text) => norm(text).includes(q)) || v.packageItems.some((i) => norm(i.name).includes(q)))
  );
}

export function packageHasTag(pkg: Pick<CateringPackage, 'tags'>, tagId: string): boolean {
  return pkg.tags.some((t) => t.id === tagId);
}

/** No tags at all (the API already drops tags deleted in Settings). */
export function packageIsUntagged(pkg: Pick<CateringPackage, 'tags'>): boolean {
  return pkg.tags.length === 0;
}

// ── Tags ────────────────────────────────────────────────────────────────────────

/**
 * Names for a set of tag ids (e.g. a line's `packageTagIds`) against the live settings list.
 * Ids that no longer exist in settings are dropped, as the BE does for `package.tags`.
 */
export function resolveTagValues(ids: readonly string[] | null | undefined, tags: readonly PackageTag[]): string[] {
  if (!ids?.length) return [];
  const byId = new Map(tags.map((t) => [t.id, t.value] as const));
  return ids.flatMap((id) => (byId.has(id) ? [byId.get(id) as string] : []));
}

/** `tagIds` to send: keep only ids that still exist in settings, without repeats. */
export function liveTagIds(ids: readonly string[], tags: readonly PackageTag[]): string[] {
  const live = new Set(tags.map((t) => t.id));
  return [...new Set(ids)].filter((id) => live.has(id));
}
